import React, { useCallback, useMemo } from "react";
import { HomeRounded } from "@mui/icons-material";
import { Feature, Point } from "geojson";
import { FeatureIdentifier } from "mapbox-gl";

import { DeleteButton, PointButton, PolygonSelectButton, RedoButton, SelectButton, ToolbarConfig, UndoButton } from "fond/map/Toolbar";
import { LayerIds } from "fond/types";
import { useAppSelector } from "fond/utils/hooks";

import DrawHandler from "../DrawHandler";

import centralOfficeDrawStyles from "./centralOfficeDrawStyles";

/**
 * Default drawing tools when drawing central offices
 */
const toolbarConfig: ToolbarConfig = [
  {
    id: "mainActions",
    type: "mode",
    items: [
      <SelectButton key="select" />,
      <PolygonSelectButton key="selectPolygon" />,
      <PolygonSelectButton key="selectLasso" selectionMode="lasso" />,
    ],
  },
  {
    id: "drawTools",
    type: "mode",
    items: [<PointButton key="drawPoint" icon={<HomeRounded />} title="Draw central office" />],
  },
  { id: "historyTools", type: "action", items: [<UndoButton key="undo" />, <RedoButton key="redo" />, <DeleteButton key="delete" />] },
];

const sources: FeatureIdentifier[] = [{ source: LayerIds.inExchange }];

/**
 * This component is responsible for managing the drawing of central office points.
 * It uses the Mapbox Draw library to provide drawing functionality and
 * manages the state of the drawn features.
 */
const CentralOfficeHandler: React.FC = () => {
  const { data } = useAppSelector((state) => state.project.projects[state.project.projectId]);

  // The initial features are derived from the data layer for central office which
  // are drawn as Points.
  const initialFeatures: Feature<Point>[] = useMemo(() => (data?.layers[LayerIds.inExchange].features || []) as Feature<Point>[], [data]);

  /**
   * Determines the initial properties for any new feature being drawn.
   */
  const getFeatureProperties = useCallback((_: Feature) => ({ layerId: LayerIds.inExchange }), []);

  return (
    <DrawHandler
      initialFeatures={initialFeatures}
      sources={sources}
      styles={centralOfficeDrawStyles}
      config={toolbarConfig}
      getFeatureProperties={getFeatureProperties}
    />
  );
};

export default CentralOfficeHandler;
